<template lang="pug">
  AppModal(:value="value" @input="$emit('input', $event)" title="Cambiar estado")
    .bulk-status
      .bulk-status__alert.bulk-status__alert--info(v-if="!result")
        | Se cambiará el estado de {{ deliveryIds.length }} envío(s) seleccionado(s).
      .bulk-status__alert.bulk-status__alert--warning(v-if="!result && approvedDeliveries.length")
        | Hay {{ approvedDeliveries.length }} envío(s) en estado "Entregado". También se actualizarán al nuevo estado.
      AppField(
        label="Nuevo estado"
        :required="true"
        :state="statusFieldState"
        invalidFeedback="Selecciona un estado"
      )
        AppSelect(v-model="selectedStatus" :options="statusOptions")
      .bulk-status__result(v-if="result")
        p.bulk-status__result-message {{ result.message }}
        div(v-if="result.failed && result.failed.length")
          p.bulk-status__result-subtitle ❌ Fallidos:
          ul.bulk-status__result-list
            li(v-for="failed in result.failed" :key="failed.id") ID {{ failed.id }}: {{ failed.error }}
        p.bulk-status__result-note(v-if="result.notFound")
          | ⚠️ {{ result.notFound }} de {{ deliveryIds.length }} envío(s) no fueron encontrados.
    template(#footer)
      AppButton(variant="secondary" outline @click="close") Cancelar
      AppButton(:disabled="!canSubmit" @click="handleSubmit")
        span(v-if="loading") Cambiando...
        span(v-else) Cambiar estado
</template>

<script>
import { mapActions } from 'vuex'
import AppModal from '@/components/UI/AppModal.vue'
import AppField from '@/components/UI/AppField.vue'
import AppSelect from '@/components/UI/AppSelect.vue'
import AppButton from '@/components/UI/AppButton.vue'

const STATUS_OPTIONS = [
  { value: 'approved', text: 'Entregado' },
  { value: 'in-transit', text: 'En tránsito' },
  { value: 'partial', text: 'Pendiente' },
  { value: 'rescheduled', text: 'Reprogramado' },
  { value: 'rejected', text: 'Rechazado' },
  { value: 'cancelled', text: 'Cancelado' }
]

export default {
  name: 'BulkSetStatusModal',
  components: { AppModal, AppField, AppSelect, AppButton },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    deliveries: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedStatus: '',
      loading: false,
      result: null,
      attempted: false
    }
  },
  computed: {
    deliveryIds() {
      return this.deliveries.map(delivery => delivery.id)
    },
    approvedDeliveries() {
      return this.deliveries.filter(delivery => delivery.status === 'approved')
    },
    statusOptions() {
      return [
        { value: '', text: 'Selecciona un estado', disabled: true },
        ...STATUS_OPTIONS
      ]
    },
    canSubmit() {
      return !this.loading && !!this.selectedStatus
    },
    statusFieldState() {
      if (this.selectedStatus) return null
      return this.attempted ? false : null
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.reset()
    }
  },
  methods: {
    ...mapActions(['bulkSetStatus']),
    reset() {
      this.selectedStatus = ''
      this.loading = false
      this.result = null
      this.attempted = false
    },
    close() {
      this.$emit('input', false)
    },
    statusLabel(value) {
      const option = STATUS_OPTIONS.find(item => item.value === value)
      return option ? option.text : value
    },
    async handleSubmit() {
      this.attempted = true
      if (!this.canSubmit) return

      const confirmed = await this.$confirmModal.confirm(
        `¿Cambiar el estado de ${this.deliveryIds.length} envío(s) a "${this.statusLabel(this.selectedStatus)}"?`,
        {
          title: 'Confirmar cambio de estado',
          confirmButton: { text: 'Cambiar', variant: 'primary' }
        }
      )
      if (!confirmed) return

      this.loading = true
      this.result = null
      try {
        const response = await this.bulkSetStatus({
          deliveryIds: this.deliveryIds,
          status: this.selectedStatus
        })
        this.result = response.data
        const updatedCount = this.result.updated ? this.result.updated.length : 0
        const failedCount = this.result.failed ? this.result.failed.length : 0

        if (failedCount > 0) {
          this.$toast.warning(`Se actualizaron ${updatedCount} envío(s). ${failedCount} fallaron.`)
          this.$emit('done')
        } else {
          this.$toast.success(`Estado actualizado en ${updatedCount} envío(s).`)
          this.$emit('done')
          this.close()
        }
      } catch (err) {
        this.$toast.danger(err.response?.data?.message || 'No se pudo cambiar el estado de los envíos.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-status__alert {
  padding: 0.75rem 1rem;
  border-radius: var(--app-border-radius, 0.25rem);
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.bulk-status__alert--info {
  background-color: var(--app-info-light, #e8f2fd);
  border-left: 4px solid var(--app-info, #50a5f1);
  color: var(--app-text-color, #374151);
}

.bulk-status__alert--warning {
  background-color: var(--app-warning-light, #fdf6e5);
  border-left: 4px solid var(--app-warning, #f1b44c);
  color: var(--app-text-color, #374151);
}

.bulk-status__result {
  margin-top: 1rem;
  font-size: 0.875rem;
}

.bulk-status__result-message {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.bulk-status__result-subtitle {
  margin-bottom: 0.25rem;
}

.bulk-status__result-list {
  margin: 0;
  padding-left: 1.25rem;
  color: var(--app-danger, #f46a6a);
}

.bulk-status__result-note {
  margin-top: 0.5rem;
  color: #6b7280;
}
</style>
